import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { ProductAnalyticsService } from 'src/app/shared/services/product-analytics.service';
import { environment } from 'src/environments/environment';
import { RenderJob } from './render-flow.model';
import { RenderOrientation } from './render.data';

// What the feedback is about: the free preview, or the paid videos.
export type RenderFeedbackStage = 'preview' | 'videos';

// The thumbs on each of the preview's frames (Figma 28721:44956 on the display, 28753:46102 on the
// phone; owner 2026-10-20, both frames 2026-10-21). The videos' form carries none.
export type RenderFeedbackRating = 'like' | 'dislike';

export interface RenderFeedbackChip {
  // What the backend stores: the labels may change, the ids do not.
  id: string;
  label: string;
}

// The designer's chips (Figma 28753:46113, 2026-10-21), in her order. "Model scale" is a chip now:
// the thumb says which frame it is about.
export const RENDER_FEEDBACK_IMPROVE: ReadonlyArray<RenderFeedbackChip> = [
  { id: 'lighting', label: 'Lighting' },
  { id: 'white_background', label: 'White background' },
  { id: 'color_presets', label: 'Color presets' },
  { id: 'render_quality', label: 'Render quality' },
  { id: 'dynamic_camera', label: 'Dynamic camera' },
  { id: 'speed', label: 'Speed' },
  { id: 'start_frame_rotation', label: 'Start frame rotation' },
  { id: 'model_scale', label: 'Model scale' },
  { id: 'price', label: 'Price' },
];

export interface RenderFeedback {
  stage: RenderFeedbackStage;
  rating: RenderFeedbackRating | null;
  improve: string[];
  // The frame whose thumb was pressed; null from the line under the videos.
  orientation: RenderOrientation | null;
  text: string;
}

// The thumbs of a model's two frames.
type PreviewRatings = Record<RenderOrientation, RenderFeedbackRating | null>;

// What the backend calls the frames: Web renders the landscape one, Mobile the portrait one.
const RATING_PLATFORM: Readonly<Record<RenderOrientation, 'web' | 'mobile'>> = { landscape: 'web', portrait: 'mobile' };

const STORAGE_KEY = '3dway.render-feedback.v2';

// Feedback on a model's preview or videos. The thumbs are the backend's (Yahor, 2026-11-05): written
// to POST /preview_rating, each frame on its own, read back from POST /preview_status. What the
// person chose or wrote has no such place: it goes through the app's POST /feedback, which emails
// the team and keeps a row in Spanner `feedback`, a readable summary in `text` and the whole record
// as JSON in `debug_data` (kind 'render_feedback', v3). A dedicated endpoint for that is still only
// a contract (POST /render_feedback, docs/render-landing-port.md, decisions 32, 37 and 55). A thumb
// alone emails nobody. That the form under the videos went out is remembered on this device.
@Injectable()
export class RenderFeedbackService {
  private readonly ratings = new Map<string, Promise<PreviewRatings>>();

  constructor(
    private readonly http: HttpClient,
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly productAnalytics: ProductAnalyticsService,
  ) {}

  // The thumb on this frame of the model's preview, as the backend has it.
  public async rating(job: RenderJob, orientation: RenderOrientation): Promise<RenderFeedbackRating | null> {
    return (await this.previewRatings(job))[orientation];
  }

  // The chosen thumb pressed again.
  public clearRating(job: RenderJob, orientation: RenderOrientation): Promise<void> {
    return this.storeRating(job, orientation, null);
  }

  public given(job: RenderJob, stage: RenderFeedbackStage): boolean {
    return !!this.stored[RenderFeedbackService.key(job, stage)];
  }

  // The thumb first: it may be sent again as it is, so a Send retried after /feedback failed stores
  // it twice and emails once.
  public async send(job: RenderJob, feedback: RenderFeedback): Promise<void> {
    if (feedback.rating && feedback.orientation) {
      await this.storeRating(job, feedback.orientation, feedback.rating);
    }
    if (feedback.improve.length || feedback.text.trim()) {
      await this.sendWords(job, feedback);
    }
    // The chips' ids and the thumb, for the analytics; the text stays with the team's email.
    this.productAnalytics.track('feedback_sent', {
      modelId: job.source.kind === 'upload' ? job.backendId : undefined,
      params: {
        stage: feedback.stage,
        rating: feedback.rating || 'none',
        orientation: feedback.orientation,
        improve: feedback.improve.join(','),
        with_text: !!feedback.text.trim(),
        sample: job.source.kind === 'sample' ? job.source.sampleId : undefined,
      },
    });
    if (!feedback.rating) {
      this.remember(RenderFeedbackService.key(job, feedback.stage));
    }
  }

  private async sendWords(job: RenderJob, feedback: RenderFeedback): Promise<void> {
    const record = {
      kind: 'render_feedback',
      v: 3,
      model_id: job.backendId || job.modelId,
      model_name: job.name,
      source: job.source.kind,
      stage: feedback.stage,
      rating: feedback.rating,
      improve: feedback.improve,
      orientation: feedback.orientation,
      text: feedback.text,
      client: {
        ua: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        url: window.location.href,
        at: new Date().toISOString(),
      },
    };
    const body = {
      user_id: await this.firebaseAuth.getUserUid(),
      text: RenderFeedbackService.summary(job, feedback),
      debug_data: JSON.stringify(record),
    };
    await lastValueFrom(this.http.post(`${environment.endpoints.webBackend}/feedback`, body, await this.backendOptions()));
  }

  // One request a model, however many frames ask.
  private previewRatings(job: RenderJob): Promise<PreviewRatings> {
    let known = this.ratings.get(job.modelId);
    if (!known) {
      known = this.loadRatings(job);
      this.ratings.set(job.modelId, known);
    }
    return known;
  }

  // Without a model on the backend (the mock) a thumb lasts as long as the page. An answer that did
  // not come leaves the thumbs empty until the next reload: nothing to stop the user over.
  private async loadRatings(job: RenderJob): Promise<PreviewRatings> {
    if (!job.backendId) {
      return { landscape: null, portrait: null };
    }
    try {
      const answer = await lastValueFrom(this.http.post<{ rating_web?: RenderFeedbackRating | null; rating_mobile?: RenderFeedbackRating | null }>(
        `${environment.endpoints.webBackend}/preview_status`,
        { user_id: await this.firebaseAuth.getUserUid(), model_id: job.backendId },
        await this.backendOptions(),
      ));
      return { landscape: answer?.rating_web || null, portrait: answer?.rating_mobile || null };
    } catch (error) {
      console.warn('Render flow: could not read the preview ratings', error);
      return { landscape: null, portrait: null };
    }
  }

  // This frame alone is taken from what was sent: an answer that overtook another may be behind on
  // the other frame.
  private async storeRating(job: RenderJob, orientation: RenderOrientation, rating: RenderFeedbackRating | null): Promise<void> {
    if (job.backendId) {
      await lastValueFrom(this.http.post(
        `${environment.endpoints.webBackend}/preview_rating`,
        { user_id: await this.firebaseAuth.getUserUid(), model_id: job.backendId, platform: RATING_PLATFORM[orientation], rating },
        await this.backendOptions(),
      ));
    }
    (await this.previewRatings(job))[orientation] = rating;
  }

  private async backendOptions(): Promise<{ headers: HttpHeaders }> {
    const token = await this.firebaseAuth.getUserToken();
    return { headers: new HttpHeaders({ Accept: 'application/json', Authorization: `Bearer ${token}` }) };
  }

  // What the team's email reads: "Render feedback · preview · portrait · Disliked · Drone 03", the
  // chips, the text. Never empty: /feedback refuses an empty text.
  private static summary(job: RenderJob, feedback: RenderFeedback): string {
    const labels = (ids: string[]) => ids.map((id) => RENDER_FEEDBACK_IMPROVE.find((chip) => chip.id === id)?.label || id).join(', ');
    const rating = feedback.rating === 'like' ? 'Liked' : feedback.rating === 'dislike' ? 'Disliked' : '';
    return [
      ['Render feedback', feedback.stage, feedback.orientation, rating, job.name].filter(Boolean).join(' · '),
      feedback.improve.length ? `Could be better: ${labels(feedback.improve)}` : '',
      feedback.text.trim(),
    ].filter(Boolean).join('\n');
  }

  private static key(job: RenderJob, stage: RenderFeedbackStage): string {
    return `${job.backendId || job.modelId}:${stage}`;
  }

  private get storageKey(): string {
    return `${STORAGE_KEY}:${this.firebaseAuth.currentUser?.uid || 'guest'}`;
  }

  private get stored(): Record<string, string> {
    try {
      const value = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    } catch {
      return {};
    }
  }

  private remember(key: string): void {
    try {
      // The newest 200: re-set, a key moves to the end.
      const entries: Array<[string, string]> = [...Object.entries(this.stored).filter(([item]) => item !== key), [key, 'sent']];
      const kept = entries.slice(-200).reduce<Record<string, string>>((all, [item, value]) => ({ ...all, [item]: value }), {});
      localStorage.setItem(this.storageKey, JSON.stringify(kept));
    } catch {
      // Private mode / blocked storage: the line asks again on the next visit.
    }
  }
}
